import React, { useState, useEffect, useRef } from 'react';

/**
 * COMPONENTE VISUAL: CARDS OVERLAY (HIJO)
 * ------------------------------------------------------------------
 * Renderiza el nuevo diseño vertical, el canvas de lluvia de tarjetas,
 * el destello (flash) y el temblor (shake) de manera optimizada.
 * ------------------------------------------------------------------
 */
const Cards = ({ data }) => {
    // --- MÁQUINA DE ESTADOS DE LA ANIMACIÓN ---
    // step 0: Oculto | step 1: Lanzamiento/Flash | step 2: Tarjeta Grande + Lluvia
    const [animStep, setAnimStep] = useState(0); 
    const [cardPayload, setCardPayload] = useState(null);
    const [isBoardVisible, setIsBoardVisible] = useState(true);

    const canvasRef = useRef(null);
    const requestRef = useRef(null);
    const particlesRef = useRef([]);
    const sequenceTimeouts = useRef([]);

    // --- ESCUCHA DEL SOCKET ---
    useEffect(() => {
        if (!data) return;

        const { type, data: payload } = data;

        if (type === 'cards.trigger_overlay') {
            const { action } = payload;

            if (action === 'show_board') { setIsBoardVisible(true); return; }
            if (action === 'hide_board') { setIsBoardVisible(false); return; }
            if (action === 'close') { abortSequence(); return; }

            if (action === 'show_card') {
                setCardPayload(payload);
                startAnimationSequence(payload);
            }
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [data]);

    // --- CONTROL DE SECUENCIA DE ANIMACIÓN ---
    const startAnimationSequence = (payload) => {
        abortSequence(); // Limpia secuencias anteriores para permitir reinicio inmediato
        
        // Paso 1: Tarjeta volando + Shake + Flash
        setAnimStep(1);

        // Paso 2: Aparece Tarjeta Principal + Inicia Lluvia
        sequenceTimeouts.current.push(setTimeout(() => {
            setAnimStep(2);
            startRain(payload);
        }, 550));
    };

    const abortSequence = () => {
        sequenceTimeouts.current.forEach(clearTimeout);
        sequenceTimeouts.current = [];
        stopRain();
        setAnimStep(0);
    };

    // --- MOTOR DEL CANVAS (LLUVIA DE TARJETAS) OPTIMIZADO ---
    const startRain = (payload) => {
        const canvas = canvasRef.current;
        if (!canvas) return;
        
        canvas.width = window.innerWidth;
        canvas.height = window.innerHeight;
        
        // Generamos solo 30 partículas para cuidar los recursos de OBS
        particlesRef.current = Array.from({ length: 30 }, () => createParticle(canvas.width, canvas.height, true));
        
        const color = payload?.cardType === 'yellow' ? '#facc15' : '#ef4444';

        const animate = () => {
            const ctx = canvas.getContext('2d');
            ctx.clearRect(0, 0, canvas.width, canvas.height);
            
            particlesRef.current.forEach(p => {
                p.y += p.speedY;
                p.x += p.speedX;
                p.angle += p.spinZ;

                if (p.y > canvas.height + 100) {
                    Object.assign(p, createParticle(canvas.width, canvas.height, false));
                }

                ctx.save();
                ctx.translate(p.x, p.y);
                ctx.rotate(p.angle);
                ctx.scale(Math.cos(p.y * p.spinX), Math.sin(p.y * p.spinY) > 0 ? 1 : 0.5);

                ctx.fillStyle = color;
                ctx.strokeStyle = 'rgba(255,255,255,0.8)';
                ctx.lineWidth = 1;

                ctx.beginPath();
                ctx.roundRect(-p.width/2, -p.height/2, p.width, p.height, 3);
                ctx.fill();
                ctx.stroke();

                ctx.restore();
            });

            requestRef.current = requestAnimationFrame(animate);
        };
        requestRef.current = requestAnimationFrame(animate);
    };

    const stopRain = () => {
        if (requestRef.current) cancelAnimationFrame(requestRef.current);
        const canvas = canvasRef.current;
        if (canvas) {
            const ctx = canvas.getContext('2d');
            ctx.clearRect(0, 0, canvas.width, canvas.height);
        }
    };

    const createParticle = (width, height, initial = false) => ({
        x: Math.random() * width,
        y: initial ? Math.random() * -height : -50,
        width: Math.random() * 15 + 15,
        height: (Math.random() * 15 + 15) * 1.4,
        speedY: Math.random() * 3 + 2,
        speedX: (Math.random() - 0.5) * 2,
        angle: Math.random() * Math.PI * 2,
        spinX: Math.random() * 0.1,
        spinY: Math.random() * 0.1,
        spinZ: Math.random() * 0.1
    });

    // --- RENDERIZADO DEL UI ---
    if (!cardPayload) return null;

    const { cardType, animationStyle, zoom = 100, player } = cardPayload;
    
    // Variables dinámicas según el tipo de tarjeta
    const isRed = cardType === 'red';
    const cardTitle = isRed ? "TARJETA ROJA" : "TARJETA AMARILLA";
    const cssModifier = isRed ? "Red" : "Yellow";
    
    // Lógica del logo de marca
    const interaktiaLogo = isRed ? "/img/overlays/cards/interaktia-blanco.png" : "/img/overlays/cards/interaktia.png";
    
    const getPlayerPhotoUrl = (p) => {
        if (!p || !p.photoUrl) return null;
        if (p.photoUrl.startsWith('http') || p.photoUrl.startsWith('data:')) {
            return p.photoUrl;
        }
        const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:50586/api/';
        const apiBase = apiUrl.replace('/api/', '');
        if (p.photoUrl.startsWith('/')) {
            return `${apiBase}${p.photoUrl}`;
        }
        return `${apiBase}/${p.photoUrl}`;
    };

    const playerPhotoPath = getPlayerPhotoUrl(player);

    // Evaluamos si usamos el contenedor general, aplicándole opacidad, zoom y el efecto de temblor (shake)
    const containerClasses = [
        "cards-overlay-wrapper",
        animStep === 1 ? "do-shake" : "",
        !isBoardVisible ? "hidden-board" : ""
    ].join(" ");

    return (
        <div className={containerClasses} style={{ '--zoom-scale': zoom / 100 }}>
            
            {/* CANVAS DE LLUVIA (Se monta sobre toda la pantalla) */}
            <canvas ref={canvasRef} className="card-rain-canvas" />

            {/* DESTELLO EN PANTALLA */}
            <div className={`flash-overlay ${animStep === 1 ? 'do-flash' : ''}`} style={{ backgroundColor: isRed ? '#ef4444' : '#facc15' }}></div>

            {/* TARJETA VOLADORA (Paso 1) */}
            {animStep === 1 && (
                <svg className={`flying-card ${animationStyle}`} viewBox="0 0 100 140" style={{ fill: isRed ? '#ef4444' : '#facc15' }}>
                    <rect x="10" y="10" width="80" height="120" rx="8" stroke="#fff" strokeWidth="3" />
                    <path d="M 20 20 L 40 20" stroke="rgba(0,0,0,0.2)" strokeWidth="4" strokeLinecap="round"/>
                    <path d="M 20 35 L 60 35" stroke="rgba(0,0,0,0.2)" strokeWidth="4" strokeLinecap="round"/>
                </svg>
            )}

            {/* GRÁFICO PRINCIPAL DE LA TARJETA VERTICAL (Paso 2) */}
            <div className={`penalty-card-wrapper ${animStep === 2 ? 'show' : ''}`}>
                <div className={`card__data card__data--${cssModifier}`} style={{
                    position: 'relative',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: '440px',
                    height: '620px',
                    borderRadius: '16px',
                    overflow: 'hidden',
                    boxShadow: 'inset 0 0 10px rgba(0,0,0,0.3), 0 0 20px rgba(0,0,0,0.3)',
                    transition: 'border-color 0.3s ease, background 0.3s ease',
                    color: isRed ? '#ffffff' : '#000000',
                    backgroundColor: isRed ? '#ef4444' : '#facc15'
                }}>
                    <p className="card__data__Title" style={{
                        position: 'absolute',
                        top: '28px',
                        left: 0,
                        right: 0,
                        fontSize: '2.2rem',
                        textTransform: 'uppercase',
                        textAlign: 'center',
                        fontWeight: '700',
                        lineHeight: '1.1',
                        margin: 0,
                        fontFamily: "'Roboto Condensed', sans-serif"
                    }}>{cardTitle}</p>

                    {/* RENDERIZADO CONDICIONAL: Foto vs No Foto */}
                    {playerPhotoPath ? (
                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', position: 'relative' }}>
                            <div className="card__data__Photo" style={{
                                border: '6px solid #fff',
                                borderRadius: '50%',
                                width: '200px',
                                height: '200px',
                                backgroundSize: 'cover',
                                backgroundPosition: 'top center',
                                backgroundRepeat: 'no-repeat',
                                boxShadow: '0 15px 30px rgba(0,0,0,0.5)',
                                marginTop: '25px',
                                backgroundImage: `url(${playerPhotoPath})`
                            }}></div>
                            <p className={`card__data__Photo__Number card__data__Photo__Number--${cssModifier}`} style={{
                                marginTop: '-55px',
                                fontFamily: "'Graduate', serif",
                                fontSize: '4rem',
                                textAlign: 'right',
                                position: 'relative',
                                zIndex: 5,
                                fontWeight: 900,
                                alignSelf: 'flex-end',
                                marginRight: '-10px',
                                marginBottom: '0',
                                color: isRed ? 'rgba(255, 255, 255, 0.9)' : 'rgba(0, 0, 0, 0.8)',
                                textShadow: isRed ? '0 4px 10px rgba(0,0,0,0.5)' : '0 4px 10px rgba(255,255,255,0.4)'
                            }}>#{player.dorsal}</p>
                        </div>
                    ) : (
                        <p className="card__data__Number" style={{
                            fontSize: '5rem',
                            lineHeight: 1,
                            margin: '20px 0',
                            fontFamily: "'Roboto Condensed', sans-serif"
                        }}>#{player.dorsal}</p>
                    )}

                    <p className="card__data__Name" style={{
                        fontSize: '2.1rem',
                        textTransform: 'uppercase',
                        fontWeight: '700',
                        lineHeight: '1.1',
                        marginTop: '8px',
                        marginBottom: 0,
                        maxWidth: '90%',
                        textAlign: 'center',
                        maxHeight: '2.2em',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                        fontFamily: "'Roboto Condensed', sans-serif"
                    }}>{player.name}</p>
                    <p className="card__data__TeamName" style={{
                        fontSize: '1.5rem',
                        textTransform: 'uppercase',
                        textAlign: 'center',
                        lineHeight: '1.1',
                        margin: '4px 0',
                        opacity: 0.9,
                        fontFamily: "'Roboto Condensed', sans-serif"
                    }}>{player.teamName}</p>
                    
                    <img src={interaktiaLogo} className="card__data__Logo" style={{
                        position: 'absolute',
                        bottom: '28px',
                        width: '110px',
                        objectFit: 'contain'
                    }} alt="Interaktia Logo" />
                </div>
            </div>

        </div>
    );
};

export { Cards };